import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, TextField, Button, Typography, Avatar, Divider,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Alert, MenuItem, Chip,
} from '@mui/material';
import { Save as SaveIcon, Lock as LockIcon, Security as SecurityIcon, Schedule as TimezoneIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import InternationalPhoneField from '../../components/common/InternationalPhoneField';
import api from '../../utils/api';
import { isAuthenticatorMethod, mfaMethodLabel } from '../../constants/mfaMethods';

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles' },
  { value: 'America/Denver', label: 'Mountain Time (MT) - Denver' },
  { value: 'America/Chicago', label: 'Central Time (CT) - Chicago' },
  { value: 'America/New_York', label: 'Eastern Time (ET) - New York' },
  { value: 'America/Phoenix', label: 'Arizona (MST) - Phoenix' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT) - Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT) - Honolulu' },
  { value: 'UTC', label: 'UTC - Coordinated Universal Time' },
  { value: 'Europe/London', label: 'GMT/BST - London' },
  { value: 'Europe/Paris', label: 'CET/CEST - Paris' },
  { value: 'Asia/Kolkata', label: 'IST - India' },
  { value: 'Asia/Singapore', label: 'SGT - Singapore' },
  { value: 'Asia/Tokyo', label: 'JST - Tokyo' },
  { value: 'Australia/Sydney', label: 'AEST/AEDT - Sydney' }
];

const Profile = () => {
  const {
    user,
    updateProfile,
    changePassword,
    setup2FA,
    verify2FA,
    enableEmailMfa,
    setupPhoneMfa,
    verifyPhoneMfa,
  } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
  });
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [twoFADialog, setTwoFADialog] = useState({
    open: false, step: 'setup', qrCode: '', secret: '', otp: '', method: '', methodLabel: '', issuer: '',
  });
  const [phoneDialog, setPhoneDialog] = useState({ open: false, method: '', otp: '', phoneHint: '' });
  const [mfaMethods, setMfaMethods] = useState([]);
  const [mfaLoading, setMfaLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMfaLoading(true);
      try {
        const res = await api.get('/settings/mfa-settings');
        if (!cancelled) {
          setMfaMethods((res.data?.data?.methods || []).filter((m) => m.enabled));
        }
      } catch {
        if (!cancelled) setMfaMethods([]);
      } finally {
        if (!cancelled) setMfaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      first_name: user?.first_name || prev.first_name,
      last_name: user?.last_name || prev.last_name,
      phone: user?.phone || prev.phone,
      timezone: user?.timezone || prev.timezone,
    }));
  }, [user?.first_name, user?.last_name, user?.phone, user?.timezone]);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const mfaConfigured = Boolean(
    user?.otp_verified || (user?.otp_enabled && user?.preferred_mfa_method === 'email_otp') || user?.mfa_phone_verified
  );

  const isMethodActive = (methodId) => {
    if (!user) return false;
    if (user.preferred_mfa_method === methodId) {
      if (isAuthenticatorMethod(methodId)) return Boolean(user.otp_verified);
      if (methodId === 'email_otp') return Boolean(user.otp_enabled && !user.otp_verified && !user.mfa_phone_verified);
      if (methodId === 'sms' || methodId === 'voice_call') return Boolean(user.mfa_phone_verified);
    }
    if (isAuthenticatorMethod(methodId) && user.otp_verified && !user.preferred_mfa_method) {
      return true;
    }
    return false;
  };

  const handleUpdateProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(formData);
      toast.success('Profile updated');
    } catch (e) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed');
      setPasswordDialog(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to change password');
    }
  };

  const handleSetupAuthenticator = async (method) => {
    try {
      const result = await setup2FA(method.id);
      setTwoFADialog({
        open: true,
        step: 'verify',
        qrCode: result.data.qrCode,
        secret: result.data.secret,
        otp: '',
        method: method.id,
        methodLabel: method.label,
        issuer: result.data.issuer || '',
      });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to setup authenticator');
    }
  };

  const handleVerify2FA = async () => {
    try {
      await verify2FA(twoFADialog.otp);
      toast.success('Authenticator MFA enabled');
      setTwoFADialog({ open: false, step: 'setup', qrCode: '', secret: '', otp: '', method: '', methodLabel: '' });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Invalid OTP');
    }
  };

  const handleEnableEmail = async () => {
    try {
      await enableEmailMfa();
      toast.success('Email OTP MFA enabled');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to enable email OTP');
    }
  };

  const handleSetupPhone = async (method) => {
    if (!formData.phone && !user?.phone) {
      toast.error('Save a mobile phone number in your profile first');
      return;
    }
    try {
      const res = await setupPhoneMfa(method.id);
      setPhoneDialog({
        open: true,
        method: method.id,
        otp: '',
        phoneHint: res.data?.phoneHint || '',
      });
      toast.info(res?.message || 'Verification code sent');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to start phone MFA');
    }
  };

  const handleVerifyPhone = async () => {
    try {
      await verifyPhoneMfa(phoneDialog.otp);
      toast.success('Phone MFA enabled');
      setPhoneDialog({ open: false, method: '', otp: '', phoneHint: '' });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Invalid OTP');
    }
  };

  const renderMethodAction = (method) => {
    const active = isMethodActive(method.id);
    if (active) {
      return <Chip size="small" color="success" label="Active" />;
    }
    if (isAuthenticatorMethod(method.id)) {
      return (
        <Button size="small" variant="outlined" onClick={() => handleSetupAuthenticator(method)} disabled={user?.otp_verified}>
          Set up
        </Button>
      );
    }
    if (method.id === 'email_otp') {
      return (
        <Button size="small" variant="outlined" onClick={handleEnableEmail}>
          Enable
        </Button>
      );
    }
    if (method.id === 'sms' || method.id === 'voice_call') {
      return (
        <Button size="small" variant="outlined" onClick={() => handleSetupPhone(method)}>
          Set up
        </Button>
      );
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Profile Settings</Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2.5rem' }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{user?.first_name} {user?.last_name}</Typography>
              <Typography color="text.secondary">{user?.email}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {user?.is_admin ? 'Administrator' : 'User'}
              </Typography>
              <Divider sx={{ my: 3 }} />
              <Button fullWidth variant="outlined" startIcon={<LockIcon />} onClick={() => setPasswordDialog(true)} sx={{ mb: 1 }}>
                Change Password
              </Button>
              <Typography variant="caption" color="text.secondary" display="block">
                MFA is required — configure a method under Security.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Personal Information</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="First Name" name="first_name" value={formData.first_name} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Email" value={user?.email} disabled />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InternationalPhoneField label="Phone" name="phone" value={formData.phone} onChange={handleChange} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Timezone"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <TimezoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    helperText="Your preferred timezone for displaying times in the application"
                  >
                    {TIMEZONES.map((tz) => (
                      <MenuItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleUpdateProfile}
                  disabled={saving}
                  sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Security — MFA</Typography>
              </Box>
              <Alert severity={mfaConfigured ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {mfaConfigured
                  ? `MFA active${user?.preferred_mfa_method ? `: ${mfaMethodLabel(user.preferred_mfa_method)}` : ''}. MFA is mandatory for this organization.`
                  : 'MFA is mandatory. Set up at least one method enabled by your administrator.'}
              </Alert>
              {mfaLoading ? (
                <CircularProgress size={28} />
              ) : mfaMethods.length === 0 ? (
                <Typography color="text.secondary">No MFA methods are configured by the administrator yet.</Typography>
              ) : (
                mfaMethods.map((method) => (
                  <Box
                    key={method.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{method.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{method.description}</Typography>
                    </Box>
                    {renderMethodAction(method)}
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Current Password" type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} sx={{ mt: 2, mb: 2 }} />
          <TextField fullWidth label="New Password" type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Confirm New Password" type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword}>Change Password</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={twoFADialog.open}
        onClose={() => setTwoFADialog({ open: false, step: 'setup', qrCode: '', secret: '', otp: '', method: '', methodLabel: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Setup {twoFADialog.methodLabel || 'Authenticator'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Scan the QR code with {twoFADialog.methodLabel || 'your authenticator app'}
            {twoFADialog.issuer ? (
              <>
                . It will appear as <strong>{twoFADialog.issuer}</strong>
              </>
            ) : (
              '.'
            )}
          </Alert>
          {twoFADialog.qrCode && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img src={twoFADialog.qrCode} alt="QR Code" style={{ maxWidth: 200 }} />
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Or enter this code manually:{' '}
            <code style={{ background: '#1a1a2e', padding: '4px 8px', borderRadius: 4 }}>{twoFADialog.secret}</code>
          </Typography>
          <TextField
            fullWidth
            label="Enter OTP from App"
            value={twoFADialog.otp}
            onChange={(e) => setTwoFADialog({ ...twoFADialog, otp: e.target.value })}
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFADialog({ open: false, step: 'setup', qrCode: '', secret: '', otp: '', method: '', methodLabel: '' })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleVerify2FA} disabled={twoFADialog.otp.length !== 6}>
            Verify & Enable
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={phoneDialog.open}
        onClose={() => setPhoneDialog({ open: false, method: '', otp: '', phoneHint: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify {mfaMethodLabel(phoneDialog.method)}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter the 6-digit code sent for {phoneDialog.phoneHint || 'your phone'}.
            Until an SMS/voice gateway is configured, the code is also emailed and written to backend logs.
          </Alert>
          <TextField
            fullWidth
            label="Verification code"
            value={phoneDialog.otp}
            onChange={(e) => setPhoneDialog({ ...phoneDialog, otp: e.target.value })}
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhoneDialog({ open: false, method: '', otp: '', phoneHint: '' })}>Cancel</Button>
          <Button variant="contained" onClick={handleVerifyPhone} disabled={phoneDialog.otp.length !== 6}>
            Verify & Enable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
